import { useCallback } from 'react';
import type {
  CustomChartDefinition,
  DashboardPresetContent,
  DashboardPresetFilters,
} from '@/entities/dashboards/model/api/types';
import { useReadCachedSharedPreset, useUpdateSharedPreset } from '@/entities/shared-presets';
import { useReadCachedSystemPreset, useUpdateSystemPreset } from '@/entities/system-presets';
import { DASHBOARD_PRESET_KEY_PARSERS, DASHBOARD_PRESETS_TABLE_NAME } from './dashboardPresetKeys';
import { resolveServerPresetKey } from './serverPresetKey';
import { isDashboardPresetFilters } from './utils/normalizeUiSettings';

/** Правка дашборда: название и половины содержимого, которые меняет запись. */
export type DashboardPresetChanges = {
  name?: string;
  charts?: CustomChartDefinition[];
  filters?: DashboardPresetFilters;
};

/** Фильтры серверного дашборда из ответа: непонятные показываются умолчанием, а не роняют панель. */
export const readServerFilters = (content: DashboardPresetContent | undefined): DashboardPresetFilters | undefined =>
  isDashboardPresetFilters(content?.filters) ? content.filters : undefined;

/**
 * Собирает содержимое серверного дашборда для записи — всегда обоими полями ({@link DashboardPresetContent}):
 * сервер заменяет содержимое полностью. Незатронутая половина — состав или фильтры — берётся из
 * записанного (`stored`).
 */
const buildServerContent = (
  stored: DashboardPresetContent | undefined,
  changes: DashboardPresetChanges,
): DashboardPresetContent | undefined => {
  if (!changes.charts && !changes.filters) return undefined;
  const charts = changes.charts ?? stored?.charts;
  // Без состава записывать нечего: пустой состав стёр бы диаграммы дашборда у всех
  if (!charts) return undefined;
  return { charts, filters: changes.filters ?? readServerFilters(stored) };
};

/** Запись серверного дашборда своей мутацией — системного либо совместного: обе принимают одно и то же. */
type ServerPresetUpdate = (variables: {
  id: number;
  name?: string;
  settings?: DashboardPresetContent;
}) => Promise<unknown>;

/**
 * Записывает серверный дашборд — системный и совместный одинаково, различаются у них только
 * хранилище записанного и мутация.
 *
 * @returns `false` — сервер запись не принял (о причине сообщил общий перехватчик) либо фильтры
 * записывать не во что.
 */
const writeServerContent = async (
  id: number,
  stored: DashboardPresetContent | undefined,
  changes: DashboardPresetChanges,
  purpose: 'charts' | 'filters',
  update: ServerPresetUpdate,
): Promise<boolean> => {
  const settings = buildServerContent(stored, changes);
  if (purpose === 'filters' && !settings) return false;
  const updated = await update({ id, name: changes.name, settings }).catch(() => null);
  return updated !== null;
};

/**
 * Запись серверного дашборда — системного или совместного — на сервере.
 *
 * Состав из режима редактирования и фильтры панели записываются своими экземплярами мутаций: смена
 * фильтров не должна выглядеть записью состава (`isUpdating`).
 */
export const useServerDashboardPresetWrite = () => {
  const { mutateAsync: updateSystemPreset, isPending: isUpdatingSystemPreset } =
    useUpdateSystemPreset<DashboardPresetContent>();
  const { mutateAsync: updateSystemFilters } = useUpdateSystemPreset<DashboardPresetContent>();
  const readCachedSystemPreset = useReadCachedSystemPreset<DashboardPresetContent>(DASHBOARD_PRESETS_TABLE_NAME);
  const { mutateAsync: updateSharedPreset, isPending: isUpdatingSharedPreset } =
    useUpdateSharedPreset<DashboardPresetContent>();
  const { mutateAsync: updateSharedFilters } = useUpdateSharedPreset<DashboardPresetContent>();
  const readCachedSharedPreset = useReadCachedSharedPreset<DashboardPresetContent>(DASHBOARD_PRESETS_TABLE_NAME);

  /**
   * Записывает содержимое и/или название серверного дашборда. Незатронутая половина содержимого
   * берётся из последнего полученного списка в момент записи: отложенная запись фильтров не должна
   * вернуть состав, записанный после её отложения.
   *
   * @param purpose - Запись состава из режима редактирования либо фильтров панели
   * @returns `null` — ключ принадлежит личному дашборду; `false` — сервер запись не принял (о причине
   * сообщил общий перехватчик) либо фильтры записывать не во что.
   */
  const writeServerPreset = useCallback(
    async (
      presetId: string,
      changes: DashboardPresetChanges,
      purpose: 'charts' | 'filters',
    ): Promise<boolean | null> => {
      const server = resolveServerPresetKey(presetId, DASHBOARD_PRESET_KEY_PARSERS);
      if (!server) return null;
      if (server.kind === 'system') {
        const update = purpose === 'charts' ? updateSystemPreset : updateSystemFilters;
        return writeServerContent(server.id, readCachedSystemPreset(server.id)?.settings, changes, purpose, update);
      }
      const update = purpose === 'charts' ? updateSharedPreset : updateSharedFilters;
      return writeServerContent(server.id, readCachedSharedPreset(server.id)?.settings, changes, purpose, update);
    },
    [
      readCachedSystemPreset,
      readCachedSharedPreset,
      updateSystemPreset,
      updateSystemFilters,
      updateSharedPreset,
      updateSharedFilters,
    ],
  );

  return {
    writeServerPreset,
    /** Запись состава серверного дашборда в работе */
    isUpdating: isUpdatingSystemPreset || isUpdatingSharedPreset,
  };
};
